import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ProductsManagement from './ProductsManagement';
import UsersManagement from './UsersManagement';
import OrdersManagement from './OrdersManagement';
import ContactsManagement from './ContactsManagement';
import InventoryManagement from './InventoryManagement';
import { getAllOrders } from '../services/orderService';
import { getAllProducts } from '../services/productService';
import { getAllContacts } from '../services/contactService';
import { getAllUsers } from '../services/userService';
import { useConfirm } from './AdminUI';
import './Dashboard.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const NOTIF_SEEN_KEY = 'adminNotifSeen';
const NAV_BADGE_SEEN_KEY = 'adminNavBadgeSeen';

const SECTION_BADGES = {
  Orders: 'pendingPayment',
  Inventory: 'lowStock',
  Contacts: 'newContacts',
};

const notificationKey = (list) => list.map((n) => `${n.id}:${n.text}`).join('|');

const ORDER_STATUS_META = [
  { id: 'pending', label: 'Pending', color: '#f59e0b' },
  { id: 'processing', label: 'Processing', color: '#3490ce' },
  { id: 'shipped', label: 'Shipped', color: '#6366f1' },
  { id: 'delivered', label: 'Delivered', color: '#10b981' },
  { id: 'cancelled', label: 'Cancelled', color: '#ef4444' },
];

const CHART_RANGES = [
  { id: 7, label: '7D' },
  { id: 14, label: '14D' },
  { id: 30, label: '30D' },
];

const NAV_GROUPS = [
  {
    label: 'General',
    items: [{ id: 'Dashboard', label: 'Overview', icon: 'fa-gauge-high' }],
  },
  {
    label: 'Sales',
    items: [{ id: 'Orders', label: 'Orders', icon: 'fa-receipt', badge: 'pendingPayment' }],
  },
  {
    label: 'Catalog',
    items: [
      { id: 'Products', label: 'Products', icon: 'fa-box' },
      { id: 'Inventory', label: 'Inventory', icon: 'fa-warehouse', badge: 'lowStock' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { id: 'Users', label: 'Users', icon: 'fa-users' },
      { id: 'Contacts', label: 'Messages', icon: 'fa-envelope', badge: 'newContacts' },
    ],
  },
];

const SECTION_META = {
  Dashboard: { title: 'Overview', subtitle: 'Sales, orders and store health at a glance' },
  Orders: { title: 'Orders', subtitle: 'Track payments and fulfil customer orders' },
  Products: { title: 'Products', subtitle: 'Manage catalog items, prices and images' },
  Inventory: { title: 'Inventory', subtitle: 'Stock levels and low-stock alerts' },
  Users: { title: 'Users', subtitle: 'Customer and administrator accounts' },
  Contacts: { title: 'Messages', subtitle: 'Leads and contact form enquiries' },
};

const formatPrice = (price) =>
  new Intl.NumberFormat('rw-RW', {
    style: 'currency',
    currency: 'RWF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(price) || 0);

const formatCompact = (value) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
    Number(value) || 0
  );

const formatOrderId = (id) => {
  if (!id) return '—';
  const value = String(id);
  if (value.startsWith('FT-')) return value;
  if (/^[a-f0-9]{24}$/i.test(value)) return value.slice(-8).toUpperCase();
  return value.toUpperCase();
};

const formatShortDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const timeAgo = (value) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return '';
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatShortDate(value);
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const percentChange = (current, previous) => {
  if (!previous) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 100);
};

const getInitials = (name) => {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const productStock = (p) => {
  const stock = Number(p.stock) || 0;
  const threshold = Number(p.lowStockThreshold) || 10;
  return { stock, threshold, alert: stock === 0 || stock <= threshold };
};

function Sparkline({ values, color = '#3490ce' }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? 100 / (values.length - 1) : 100;
  const points = values.map((v, i) => `${(i * step).toFixed(2)},${(30 - (v / max) * 26 - 2).toFixed(2)}`);
  const area = `0,30 ${points.join(' ')} 100,30`;
  return (
    <svg className="adm-spark" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={points.join(' ')} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function TrendBadge({ value }) {
  if (value === null) return null;
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const icon = dir === 'up' ? 'fa-arrow-trend-up' : dir === 'down' ? 'fa-arrow-trend-down' : 'fa-minus';
  return (
    <span className={`adm-trend adm-trend--${dir}`}>
      <i className={`fas ${icon}`} aria-hidden="true"></i>
      {Math.abs(value)}%
    </span>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('Dashboard');
  const [displaySection, setDisplaySection] = useState('Dashboard');
  const [contentAnim, setContentAnim] = useState('enter');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('adminSidebarCollapsed') === '1'
  );
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [seenNotifKey, setSeenNotifKey] = useState(
    () => localStorage.getItem(NOTIF_SEEN_KEY) || ''
  );
  const [seenBadges, setSeenBadges] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(NAV_BADGE_SEEN_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const [chartRange, setChartRange] = useState(14);
  const [data, setData] = useState({ products: [], users: [], orders: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const animTimer = useRef(null);
  const searchRef = useRef(null);
  const menusRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (animTimer.current) clearTimeout(animTimer.current);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    const handleClick = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
      if (menusRef.current && !menusRef.current.contains(event.target)) setOpenMenu(null);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [products, users, orders, contacts] = await Promise.all([
        getAllProducts().catch(() => []),
        getAllUsers().catch(() => []),
        getAllOrders(token).catch(() => []),
        getAllContacts({}, token).catch(() => []),
      ]);
      setData({
        products: Array.isArray(products) ? products : [],
        users: Array.isArray(users) ? users : [],
        orders: Array.isArray(orders) ? orders : [],
        contacts: Array.isArray(contacts) ? contacts : [],
      });
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (displaySection === 'Dashboard') loadData();
  }, [displaySection, loadData]);

  const stats = useMemo(() => {
    const { products, orders, contacts } = data;
    const now = Date.now();
    const inWindow = (value, from, to) => {
      const t = new Date(value).getTime();
      return !Number.isNaN(t) && t >= from && t < to;
    };
    const paid = orders.filter((o) => o.paymentStatus === 'paid');
    const revenue = paid.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
    const sumTotals = (list) => list.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
    const payingCustomerKey = (order) => {
      const email = String(order.customerEmail || order.user?.email || '').trim().toLowerCase();
      if (email) return `email:${email}`;
      const userId = order.user?.id || order.user?._id;
      if (userId) return `user:${userId}`;
      const phone = String(order.phone || '').replace(/\D/g, '');
      if (phone.length >= 8) return `phone:${phone}`;
      const name = String(order.customerName || order.user?.name || '').trim().toLowerCase();
      if (name) return `name:${name}`;
      return `order:${order.id || order._id}`;
    };
    const firstPaidAt = new Map();
    paid.forEach((order) => {
      const t = new Date(order.createdAt).getTime();
      if (Number.isNaN(t)) return;
      const key = payingCustomerKey(order);
      const previous = firstPaidAt.get(key);
      if (previous == null || t < previous) firstPaidAt.set(key, t);
    });

    const cur30 = [now - 30 * DAY_MS, now + DAY_MS];
    const prev30 = [now - 60 * DAY_MS, now - 30 * DAY_MS];
    const paidCur = paid.filter((o) => inWindow(o.createdAt, ...cur30));
    const paidPrev = paid.filter((o) => inWindow(o.createdAt, ...prev30));
    const ordersCur = orders.filter((o) => inWindow(o.createdAt, ...cur30));
    const ordersPrev = orders.filter((o) => inWindow(o.createdAt, ...prev30));
    const customersNew = [...firstPaidAt.values()].filter((t) => t >= cur30[0] && t < cur30[1]).length;
    const customersPrev = [...firstPaidAt.values()].filter((t) => t >= prev30[0] && t < prev30[1]).length;

    const todayStart = startOfDay(new Date()).getTime();
    const ordersToday = orders.filter((o) => inWindow(o.createdAt, todayStart, now + DAY_MS)).length;

    const aovCur = paidCur.length ? sumTotals(paidCur) / paidCur.length : 0;
    const aovPrev = paidPrev.length ? sumTotals(paidPrev) / paidPrev.length : 0;

    return {
      revenue,
      paidCount: paid.length,
      revenueTrend: percentChange(sumTotals(paidCur), sumTotals(paidPrev)),
      orders: orders.length,
      ordersToday,
      ordersTrend: percentChange(ordersCur.length, ordersPrev.length),
      aov: paid.length ? revenue / paid.length : 0,
      aovTrend: percentChange(aovCur, aovPrev),
      customers: firstPaidAt.size,
      customersNew,
      customersTrend: percentChange(customersNew, customersPrev),
      products: products.length,
      pendingPayment: orders.filter((o) => o.paymentStatus === 'pending').length,
      toFulfil: orders.filter((o) => o.status === 'pending' || o.status === 'processing').length,
      lowStock: products.filter((p) => productStock(p).alert).length,
      outOfStock: products.filter((p) => (Number(p.stock) || 0) === 0).length,
      newContacts: contacts.filter((c) => c.status === 'new').length,
    };
  }, [data]);

  const dailySeries = useMemo(() => {
    const days = 30;
    const today = startOfDay(new Date());
    const buckets = Array.from({ length: days }, (_, i) => {
      const date = new Date(today.getTime() - (days - 1 - i) * DAY_MS);
      return { date, sales: 0, orders: 0, paid: 0 };
    });
    data.orders.forEach((o) => {
      const t = startOfDay(o.createdAt).getTime();
      const index = Math.round((t - buckets[0].date.getTime()) / DAY_MS);
      if (index < 0 || index >= days) return;
      buckets[index].orders += 1;
      if (o.status !== 'cancelled') buckets[index].sales += Number(o.total) || 0;
      if (o.paymentStatus === 'paid') buckets[index].paid += Number(o.total) || 0;
    });
    return buckets;
  }, [data.orders]);

  const chart = useMemo(() => {
    const series = dailySeries.slice(-chartRange);
    const total = series.reduce((sum, d) => sum + d.sales, 0);
    const orderCount = series.reduce((sum, d) => sum + d.orders, 0);
    const max = Math.max(...series.map((d) => d.sales), 0);
    let niceMax = 4;
    if (max > 0) {
      const step = max / 4;
      const magnitude = 10 ** Math.floor(Math.log10(step));
      niceMax = Math.ceil(step / magnitude) * magnitude * 4;
    }
    const labelEvery = chartRange <= 7 ? 1 : chartRange <= 14 ? 2 : 5;
    return { series, total, orderCount, max: niceMax, labelEvery };
  }, [dailySeries, chartRange]);

  const statusBreakdown = useMemo(() => {
    const total = data.orders.length;
    const rows = ORDER_STATUS_META.map((s) => ({
      ...s,
      count: data.orders.filter((o) => o.status === s.id).length,
    }));
    let offset = 0;
    const circumference = 2 * Math.PI * 42;
    const arcs = rows
      .filter((r) => r.count > 0)
      .map((r) => {
        const length = (r.count / total) * circumference;
        const arc = { ...r, dash: `${length} ${circumference - length}`, offset: -offset };
        offset += length;
        return arc;
      });
    return { total, rows, arcs };
  }, [data.orders]);

  const recentOrders = useMemo(
    () =>
      [...data.orders]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 6),
    [data.orders]
  );

  const stockAlerts = useMemo(
    () =>
      data.products
        .map((p) => ({ ...p, ...productStock(p) }))
        .filter((p) => p.alert)
        .sort((a, b) => a.stock - b.stock)
        .slice(0, 5),
    [data.products]
  );

  const topProducts = useMemo(() => {
    const totals = new Map();
    data.orders
      .filter((o) => o.status !== 'cancelled')
      .forEach((o) => {
        (o.items || []).forEach((item) => {
          const name = item.name || item.title || item.product?.title || 'Product';
          const image = item.image || item.product?.image;
          const entry = totals.get(name) || { name, image, qty: 0, revenue: 0 };
          const qty = Number(item.quantity) || 1;
          entry.qty += qty;
          entry.revenue += (Number(item.price) || 0) * qty;
          if (!entry.image && image) entry.image = image;
          totals.set(name, entry);
        });
      });
    let list = [...totals.values()].sort((a, b) => b.qty - a.qty);
    if (!list.length) {
      list = data.products
        .map((p) => ({
          name: p.title || p.name,
          image: p.image,
          qty: parseInt(String(p.sold || '0').replace(/\D/g, ''), 10) || 0,
          revenue: null,
        }))
        .filter((p) => p.qty > 0)
        .sort((a, b) => b.qty - a.qty);
    }
    const top = list.slice(0, 5);
    const maxQty = Math.max(...top.map((p) => p.qty), 1);
    return top.map((p) => ({ ...p, share: Math.round((p.qty / maxQty) * 100) }));
  }, [data.orders, data.products]);

  const recentMessages = useMemo(
    () =>
      [...data.contacts]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 4),
    [data.contacts]
  );

  const notifications = useMemo(() => {
    const list = [];
    if (stats.pendingPayment > 0) {
      list.push({
        id: 'pay',
        icon: 'fa-coins',
        tone: 'amber',
        text: `${stats.pendingPayment} order${stats.pendingPayment === 1 ? '' : 's'} awaiting payment confirmation`,
        section: 'Orders',
      });
    }
    if (stats.lowStock > 0) {
      list.push({
        id: 'stock',
        icon: 'fa-triangle-exclamation',
        tone: 'red',
        text: `${stats.lowStock} product${stats.lowStock === 1 ? '' : 's'} low or out of stock`,
        section: 'Inventory',
      });
    }
    if (stats.newContacts > 0) {
      list.push({
        id: 'msg',
        icon: 'fa-envelope',
        tone: 'blue',
        text: `${stats.newContacts} new message${stats.newContacts === 1 ? '' : 's'} from the website`,
        section: 'Contacts',
      });
    }
    return list;
  }, [stats]);

  const unseenNotifications = useMemo(() => {
    const key = notificationKey(notifications);
    if (!key || key === seenNotifKey) return [];
    return notifications;
  }, [notifications, seenNotifKey]);

  const markNotificationsSeen = () => {
    const key = notificationKey(notifications);
    setSeenNotifKey(key);
    localStorage.setItem(NOTIF_SEEN_KEY, key);
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const results = [];
    Object.entries(SECTION_META).forEach(([id, m]) => {
      if (m.title.toLowerCase().includes(q)) {
        results.push({ key: `s-${id}`, type: 'Page', label: m.title, icon: 'fa-arrow-right', section: id });
      }
    });
    data.orders
      .filter((o) =>
        [o.id, o.customerName, o.customerEmail, o.phone, o.user?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      )
      .slice(0, 4)
      .forEach((o) =>
        results.push({
          key: `o-${o.id}`,
          type: 'Order',
          label: `#${formatOrderId(o.id)} · ${o.customerName || o.user?.name || 'Customer'}`,
          icon: 'fa-receipt',
          section: 'Orders',
        })
      );
    data.products
      .filter((p) => [p.title, p.name, p.category].filter(Boolean).join(' ').toLowerCase().includes(q))
      .slice(0, 4)
      .forEach((p) =>
        results.push({
          key: `p-${p.id || p._id}`,
          type: 'Product',
          label: p.title || p.name,
          icon: 'fa-box',
          section: 'Products',
        })
      );
    data.contacts
      .filter((c) => [c.fullName, c.email, c.subject].filter(Boolean).join(' ').toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((c) =>
        results.push({
          key: `c-${c.id || c._id}`,
          type: 'Message',
          label: `${c.fullName} · ${c.subject || 'Enquiry'}`,
          icon: 'fa-envelope',
          section: 'Contacts',
        })
      );
    return results;
  }, [searchQuery, data]);

  const handleLogout = () => {
    setOpenMenu(null);
    confirm({
      tone: 'info',
      icon: 'fa-arrow-right-from-bracket',
      title: 'Log out of Fabritech Admin?',
      message: "You'll need to sign in again to manage orders, products and customers.",
      item: user
        ? {
            media: <span className="ax-avatar ax-avatar--lg">{getInitials(user.name || user.email)}</span>,
            title: user.name || 'Administrator',
            meta: user.email || 'Signed in',
          }
        : null,
      confirmLabel: 'Log out',
      cancelLabel: 'Stay signed in',
      onConfirm: () => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        navigate('/login');
      },
    });
  };

  const markSectionSeen = (section) => {
    const badgeKey = SECTION_BADGES[section];
    if (!badgeKey) return;
    const next = { ...seenBadges, [badgeKey]: stats[badgeKey] || 0 };
    setSeenBadges(next);
    localStorage.setItem(NAV_BADGE_SEEN_KEY, JSON.stringify(next));
  };

  const navBadgeCount = (item) => {
    if (!item.badge || activeSection === item.id) return 0;
    const current = stats[item.badge] || 0;
    const seen = Number(seenBadges[item.badge] || 0);
    return current > seen ? current : 0;
  };

  const selectSection = (section) => {
    markSectionSeen(section);
    setOpenMenu(null);
    setSearchOpen(false);
    if (section === activeSection) {
      setSidebarOpen(false);
      return;
    }

    setActiveSection(section);
    setSidebarOpen(false);
    setContentAnim('exit');

    if (animTimer.current) clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => {
      setDisplaySection(section);
      setContentAnim('enter');
    }, 180);
  };

  const meta = SECTION_META[activeSection] || SECTION_META.Dashboard;
  const firstName = user?.name ? user.name.split(' ')[0] : 'Admin';
  const todayLabel = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const value = (v) => (loading && !lastUpdated ? '—' : v);

  const kpis = [
    {
      id: 'revenue',
      label: 'Paid revenue',
      value: formatPrice(stats.revenue),
      trend: stats.revenueTrend,
      foot: `${stats.paidCount} paid order${stats.paidCount === 1 ? '' : 's'}`,
      icon: 'fa-coins',
      tone: 'blue',
      spark: dailySeries.slice(-14).map((d) => d.paid),
      section: 'Orders',
    },
    {
      id: 'orders',
      label: 'Total orders',
      value: stats.orders.toLocaleString(),
      trend: stats.ordersTrend,
      foot: `${stats.ordersToday} placed today`,
      icon: 'fa-receipt',
      tone: 'indigo',
      spark: dailySeries.slice(-14).map((d) => d.orders),
      section: 'Orders',
    },
    {
      id: 'aov',
      label: 'Avg. order value',
      value: formatPrice(stats.aov),
      trend: stats.aovTrend,
      foot: 'Based on paid orders',
      icon: 'fa-chart-line',
      tone: 'green',
      section: 'Orders',
    },
    {
      id: 'customers',
      label: 'Customers',
      value: stats.customers.toLocaleString(),
      trend: stats.customersTrend,
      foot: `${stats.customersNew} paid in last 30 days`,
      icon: 'fa-users',
      tone: 'teal',
      section: 'Orders',
    },
  ];

  const attention = [
    { id: 'pay', label: 'Awaiting payment', value: stats.pendingPayment, icon: 'fa-hourglass-half', tone: 'amber', section: 'Orders' },
    { id: 'fulfil', label: 'To fulfil', value: stats.toFulfil, icon: 'fa-truck-fast', tone: 'blue', section: 'Orders' },
    { id: 'stock', label: 'Stock alerts', value: stats.lowStock, icon: 'fa-triangle-exclamation', tone: 'red', section: 'Inventory' },
    { id: 'msg', label: 'New messages', value: stats.newContacts, icon: 'fa-envelope-open-text', tone: 'violet', section: 'Contacts' },
  ];

  const quickActions = [
    { label: 'Add product', icon: 'fa-plus', section: 'Products' },
    { label: 'Update stock', icon: 'fa-boxes-stacked', section: 'Inventory' },
    { label: 'Process orders', icon: 'fa-clipboard-check', section: 'Orders' },
    { label: 'Reply to messages', icon: 'fa-reply', section: 'Contacts' },
  ];

  return (
    <div className={`adm ${collapsed ? 'adm--collapsed' : ''}`}>
      {sidebarOpen && (
        <div className="adm-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside className={`adm-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="adm-sidebar__brand">
          <Link to="/" className="adm-brand" aria-label="Fabritech home">
            <span className="adm-brand__mark">
              <img src="/assets/img/logo/F_logo.png" alt="" />
            </span>
            <span className="adm-brand__text">
              <strong>Fabritech</strong>
              <small>Admin console</small>
            </span>
          </Link>
          <button
            className="adm-sidebar__close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            type="button"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <nav className="adm-nav" aria-label="Dashboard">
          {NAV_GROUPS.map((group) => (
            <div className="adm-nav__group" key={group.label}>
              <p className="adm-nav__label">{group.label}</p>
              <ul>
                {group.items.map((item) => {
                  const badge = navBadgeCount(item);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`adm-nav__item ${activeSection === item.id ? 'is-active' : ''}`}
                        onClick={() => selectSection(item.id)}
                        title={collapsed ? item.label : undefined}
                      >
                        <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                        <span className="adm-nav__text">{item.label}</span>
                        {badge > 0 ? <span className="adm-nav__badge">{badge}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="adm-sidebar__foot">
          <Link to="/shop" className="adm-nav__item adm-nav__item--link" title={collapsed ? 'View store' : undefined}>
            <i className="fas fa-store" aria-hidden="true"></i>
            <span className="adm-nav__text">View store</span>
            <i className="fas fa-arrow-up-right-from-square adm-nav__ext" aria-hidden="true"></i>
          </Link>
          <div className="adm-user">
            <span className="adm-avatar">{getInitials(user?.name)}</span>
            <span className="adm-user__info">
              <strong>{user?.name || 'Administrator'}</strong>
              <small>{user?.email || user?.role || 'Admin'}</small>
            </span>
            <button type="button" className="adm-user__logout" onClick={handleLogout} title="Logout" aria-label="Logout">
              <i className="fas fa-arrow-right-from-bracket" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </aside>

      <div className="adm-main">
        <header className="adm-topbar">
          <div className="adm-topbar__left">
            <button
              className="adm-icon-btn adm-topbar__menu"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              type="button"
            >
              <i className="fas fa-bars" aria-hidden="true"></i>
            </button>
            <button
              className="adm-icon-btn adm-topbar__collapse"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              type="button"
            >
              <i className={`fas ${collapsed ? 'fa-angles-right' : 'fa-angles-left'}`} aria-hidden="true"></i>
            </button>
            <nav className="adm-crumbs" aria-label="Breadcrumb">
              <span>Admin</span>
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
              <strong>{meta.title}</strong>
            </nav>
          </div>

          <div className="adm-search" ref={searchRef}>
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Search orders, products, messages…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              aria-label="Search dashboard"
            />
            {searchOpen && searchQuery.trim() ? (
              <div className="adm-dropdown adm-search__results">
                {searchResults.length ? (
                  searchResults.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      className="adm-search__result"
                      onClick={() => {
                        setSearchQuery('');
                        selectSection(r.section);
                      }}
                    >
                      <i className={`fas ${r.icon}`} aria-hidden="true"></i>
                      <span>{r.label}</span>
                      <small>{r.type}</small>
                    </button>
                  ))
                ) : (
                  <p className="adm-dropdown__empty">No results for “{searchQuery.trim()}”</p>
                )}
              </div>
            ) : null}
          </div>

          <div className="adm-topbar__right" ref={menusRef}>
            <div className="adm-menu">
              <button
                type="button"
                className={`adm-icon-btn ${openMenu === 'notif' ? 'is-active' : ''}`}
                aria-label="Notifications"
                aria-expanded={openMenu === 'notif'}
                onClick={() => {
                  if (openMenu === 'notif') {
                    setOpenMenu(null);
                    return;
                  }
                  markNotificationsSeen();
                  setOpenMenu('notif');
                }}
              >
                <i className="far fa-bell" aria-hidden="true"></i>
                {unseenNotifications.length ? (
                  <span className="adm-icon-btn__dot">{unseenNotifications.length}</span>
                ) : null}
              </button>
              {openMenu === 'notif' ? (
                <div className="adm-dropdown adm-dropdown--notif">
                  <div className="adm-dropdown__head">
                    <strong>Notifications</strong>
                    <span>{notifications.length} active</span>
                  </div>
                  {notifications.length ? (
                    notifications.map((n) => (
                      <button key={n.id} type="button" className="adm-notif" onClick={() => selectSection(n.section)}>
                        <span className={`adm-notif__icon adm-tone--${n.tone}`}>
                          <i className={`fas ${n.icon}`} aria-hidden="true"></i>
                        </span>
                        <span>{n.text}</span>
                      </button>
                    ))
                  ) : (
                    <p className="adm-dropdown__empty">You're all caught up.</p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="adm-menu">
              <button
                type="button"
                className="adm-profile"
                aria-expanded={openMenu === 'profile'}
                onClick={() => setOpenMenu(openMenu === 'profile' ? null : 'profile')}
              >
                <span className="adm-avatar adm-avatar--sm">{getInitials(user?.name)}</span>
                <span className="adm-profile__name">{user?.name || 'Admin'}</span>
                <i className="fas fa-chevron-down" aria-hidden="true"></i>
              </button>
              {openMenu === 'profile' ? (
                <div className="adm-dropdown adm-dropdown--profile">
                  <div className="adm-dropdown__user">
                    <span className="adm-avatar">{getInitials(user?.name)}</span>
                    <span>
                      <strong>{user?.name || 'Administrator'}</strong>
                      <small>{user?.email || 'Admin account'}</small>
                    </span>
                  </div>
                  <Link to="/" className="adm-dropdown__link">
                    <i className="fas fa-house" aria-hidden="true"></i> Website home
                  </Link>
                  <Link to="/shop" className="adm-dropdown__link">
                    <i className="fas fa-store" aria-hidden="true"></i> View store
                  </Link>
                  <button type="button" className="adm-dropdown__link is-danger" onClick={handleLogout}>
                    <i className="fas fa-arrow-right-from-bracket" aria-hidden="true"></i> Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <div className="adm-content">
          <div className={`adm-page adm-page--${contentAnim}`}>
            <div className="adm-pagehead">
              <div>
                <h1>{displaySection === 'Dashboard' ? `${getGreeting()}, ${firstName}` : meta.title}</h1>
                <p>{displaySection === 'Dashboard' ? todayLabel : meta.subtitle}</p>
              </div>
              {displaySection === 'Dashboard' ? (
                <div className="adm-pagehead__actions">
                  <span className="adm-updated">
                    {lastUpdated
                      ? `Updated ${lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                      : 'Loading…'}
                  </span>
                  <button type="button" className="adm-btn adm-btn--ghost" onClick={loadData} disabled={loading}>
                    <i className={`fas fa-rotate ${loading ? 'fa-spin' : ''}`} aria-hidden="true"></i>
                    Refresh
                  </button>
                  <button type="button" className="adm-btn adm-btn--primary" onClick={() => selectSection('Products')}>
                    <i className="fas fa-plus" aria-hidden="true"></i>
                    Add product
                  </button>
                </div>
              ) : null}
            </div>

            {displaySection === 'Dashboard' && (
              <section className="adm-overview">
                <div className="adm-kpis">
                  {kpis.map((k, i) => (
                    <button
                      key={k.id}
                      type="button"
                      className="adm-card adm-kpi"
                      style={{ '--stagger': i }}
                      onClick={() => selectSection(k.section)}
                    >
                      <div className="adm-kpi__top">
                        <span className={`adm-kpi__icon adm-tone--${k.tone}`}>
                          <i className={`fas ${k.icon}`} aria-hidden="true"></i>
                        </span>
                        <TrendBadge value={k.trend} />
                      </div>
                      <p className="adm-kpi__label">{k.label}</p>
                      <p className="adm-kpi__value">{value(k.value)}</p>
                      <div className="adm-kpi__foot">
                        <span>{k.foot}</span>
                        {k.spark ? <Sparkline values={k.spark} /> : null}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="adm-attention">
                  {attention.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={`adm-attention__item ${a.value > 0 ? 'has-items' : ''}`}
                      onClick={() => selectSection(a.section)}
                    >
                      <span className={`adm-attention__icon adm-tone--${a.tone}`}>
                        <i className={`fas ${a.icon}`} aria-hidden="true"></i>
                      </span>
                      <span className="adm-attention__text">
                        <strong>{value(a.value)}</strong>
                        <small>{a.label}</small>
                      </span>
                      <i className="fas fa-chevron-right adm-attention__go" aria-hidden="true"></i>
                    </button>
                  ))}
                </div>

                <div className="adm-grid adm-grid--main">
                  <article className="adm-card adm-chart">
                    <header className="adm-card__head">
                      <div>
                        <h2>Sales performance</h2>
                        <p>Order value excluding cancelled orders</p>
                      </div>
                      <div className="adm-segment" role="tablist" aria-label="Chart range">
                        {CHART_RANGES.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            role="tab"
                            aria-selected={chartRange === r.id}
                            className={chartRange === r.id ? 'is-active' : ''}
                            onClick={() => setChartRange(r.id)}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </header>
                    <div className="adm-chart__summary">
                      <div>
                        <small>Sales</small>
                        <strong>{formatPrice(chart.total)}</strong>
                      </div>
                      <div>
                        <small>Orders</small>
                        <strong>{chart.orderCount}</strong>
                      </div>
                      <div>
                        <small>Daily average</small>
                        <strong>{formatPrice(chart.total / chartRange)}</strong>
                      </div>
                    </div>
                    <div className="adm-chart__plot">
                      <div className="adm-chart__axis" aria-hidden="true">
                        {[4, 3, 2, 1, 0].map((n) => (
                          <span key={n}>{formatCompact((chart.max / 4) * n)}</span>
                        ))}
                      </div>
                      <div className="adm-chart__area">
                        <div className="adm-chart__grid" aria-hidden="true">
                          {[0, 1, 2, 3, 4].map((n) => (
                            <span key={n} />
                          ))}
                        </div>
                        <div className="adm-chart__bars" style={{ '--bars': chart.series.length }}>
                          {chart.series.map((d, i) => {
                            const label = d.date.toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                            });
                            return (
                              <div className="adm-chart__col" key={d.date.getTime()}>
                                <div
                                  className="adm-chart__bar"
                                  style={{ height: `calc((100% - 24px) * ${chart.max ? d.sales / chart.max : 0})` }}
                                >
                                  <span className="adm-chart__tip">
                                    <strong>{formatPrice(d.sales)}</strong>
                                    {label} · {d.orders} order{d.orders === 1 ? '' : 's'}
                                  </span>
                                </div>
                                <span className="adm-chart__x">
                                  {(chart.series.length - 1 - i) % chart.labelEvery === 0
                                    ? chartRange <= 7
                                      ? d.date.toLocaleDateString('en-GB', { weekday: 'short' })
                                      : label
                                    : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </article>

                  <article className="adm-card adm-status">
                    <header className="adm-card__head">
                      <div>
                        <h2>Order status</h2>
                        <p>All-time fulfilment breakdown</p>
                      </div>
                    </header>
                    <div className="adm-donut">
                      <svg viewBox="0 0 100 100" aria-hidden="true">
                        <circle cx="50" cy="50" r="42" className="adm-donut__track" />
                        {statusBreakdown.arcs.map((a) => (
                          <circle
                            key={a.id}
                            cx="50"
                            cy="50"
                            r="42"
                            className="adm-donut__arc"
                            stroke={a.color}
                            strokeDasharray={a.dash}
                            strokeDashoffset={a.offset}
                          />
                        ))}
                      </svg>
                      <div className="adm-donut__center">
                        <strong>{value(statusBreakdown.total)}</strong>
                        <small>Orders</small>
                      </div>
                    </div>
                    <ul className="adm-legend">
                      {statusBreakdown.rows.map((r) => (
                        <li key={r.id}>
                          <span className="adm-legend__dot" style={{ background: r.color }} />
                          <span className="adm-legend__label">{r.label}</span>
                          <strong>{r.count}</strong>
                          <small>
                            {statusBreakdown.total ? Math.round((r.count / statusBreakdown.total) * 100) : 0}%
                          </small>
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>

                <div className="adm-grid adm-grid--main">
                  <article className="adm-card adm-card--flush">
                    <header className="adm-card__head">
                      <div>
                        <h2>Recent orders</h2>
                        <p>Latest purchases from the online store</p>
                      </div>
                      <button type="button" className="adm-link" onClick={() => selectSection('Orders')}>
                        View all <i className="fas fa-arrow-right" aria-hidden="true"></i>
                      </button>
                    </header>
                    {recentOrders.length ? (
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead>
                            <tr>
                              <th>Order</th>
                              <th>Customer</th>
                              <th>Payment</th>
                              <th>Status</th>
                              <th className="is-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentOrders.map((o) => (
                              <tr key={o.id} onClick={() => selectSection('Orders')}>
                                <td>
                                  <strong className="adm-table__id">#{formatOrderId(o.id)}</strong>
                                  <small>
                                    {formatShortDate(o.createdAt)} · {(o.items || []).length} item
                                    {(o.items || []).length === 1 ? '' : 's'}
                                  </small>
                                </td>
                                <td>
                                  <span className="adm-table__customer">
                                    <span className="adm-avatar adm-avatar--xs">
                                      {getInitials(o.customerName || o.user?.name || 'C')}
                                    </span>
                                    <span>
                                      {o.customerName || o.user?.name || '—'}
                                      <small>{o.phone || o.customerEmail || o.user?.email || ''}</small>
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  <span className={`adm-pill adm-pill--pay-${o.paymentStatus || 'pending'}`}>
                                    {o.paymentStatus || 'pending'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`adm-pill adm-pill--${o.status || 'pending'}`}>
                                    {o.status || 'pending'}
                                  </span>
                                </td>
                                <td className="is-right">
                                  <strong>{formatPrice(o.total)}</strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="adm-empty">
                        <i className="fas fa-receipt" aria-hidden="true"></i>
                        <p>{loading ? 'Loading orders…' : 'No orders yet. New purchases will appear here.'}</p>
                      </div>
                    )}
                  </article>

                  <article className="adm-card">
                    <header className="adm-card__head">
                      <div>
                        <h2>Stock alerts</h2>
                        <p>
                          {stats.outOfStock} out of stock · {Math.max(stats.lowStock - stats.outOfStock, 0)} running low
                        </p>
                      </div>
                      <button type="button" className="adm-link" onClick={() => selectSection('Inventory')}>
                        Manage
                      </button>
                    </header>
                    {stockAlerts.length ? (
                      <ul className="adm-list">
                        {stockAlerts.map((p) => (
                          <li key={p.id || p._id} className="adm-list__item">
                            <span className="adm-thumb">
                              {p.image ? <img src={p.image} alt="" loading="lazy" /> : <i className="fas fa-box" aria-hidden="true"></i>}
                            </span>
                            <span className="adm-list__main">
                              <strong>{p.title || p.name}</strong>
                              <small>{p.category || 'Uncategorised'}</small>
                              <span className="adm-meter">
                                <span
                                  style={{ width: `${Math.min((p.stock / (p.threshold * 2)) * 100, 100)}%` }}
                                  className={p.stock === 0 ? 'is-out' : 'is-low'}
                                />
                              </span>
                            </span>
                            <span className={`adm-stock ${p.stock === 0 ? 'is-out' : 'is-low'}`}>
                              {p.stock === 0 ? 'Out' : `${p.stock} left`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="adm-empty adm-empty--ok">
                        <i className="fas fa-circle-check" aria-hidden="true"></i>
                        <p>{loading ? 'Checking stock…' : 'All products are well stocked.'}</p>
                      </div>
                    )}
                  </article>
                </div>

                <div className="adm-grid adm-grid--three">
                  <article className="adm-card">
                    <header className="adm-card__head">
                      <div>
                        <h2>Top products</h2>
                        <p>{topProducts.length && topProducts[0].revenue !== null ? 'By units ordered' : 'By units sold'}</p>
                      </div>
                    </header>
                    {topProducts.length ? (
                      <ol className="adm-rank">
                        {topProducts.map((p, i) => (
                          <li key={p.name}>
                            <span className="adm-rank__pos">{i + 1}</span>
                            <span className="adm-thumb adm-thumb--sm">
                              {p.image ? <img src={p.image} alt="" loading="lazy" /> : <i className="fas fa-box" aria-hidden="true"></i>}
                            </span>
                            <span className="adm-rank__main">
                              <span className="adm-rank__row">
                                <strong>{p.name}</strong>
                                <small>{p.qty} units</small>
                              </span>
                              <span className="adm-meter">
                                <span style={{ width: `${p.share}%` }} />
                              </span>
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="adm-empty">
                        <i className="fas fa-chart-simple" aria-hidden="true"></i>
                        <p>{loading ? 'Loading…' : 'Sales data will appear once orders come in.'}</p>
                      </div>
                    )}
                  </article>

                  <article className="adm-card">
                    <header className="adm-card__head">
                      <div>
                        <h2>Latest messages</h2>
                        <p>{stats.newContacts} unread</p>
                      </div>
                      <button type="button" className="adm-link" onClick={() => selectSection('Contacts')}>
                        Inbox
                      </button>
                    </header>
                    {recentMessages.length ? (
                      <ul className="adm-list">
                        {recentMessages.map((c) => (
                          <li
                            key={c.id || c._id}
                            className="adm-list__item adm-list__item--click"
                            onClick={() => selectSection('Contacts')}
                          >
                            <span className="adm-avatar adm-avatar--sm adm-avatar--soft">{getInitials(c.fullName)}</span>
                            <span className="adm-list__main">
                              <strong>
                                {c.fullName}
                                {c.status === 'new' ? <em className="adm-new-dot" aria-label="Unread" /> : null}
                              </strong>
                              <small>{c.subject || c.serviceInterest || 'General enquiry'}</small>
                            </span>
                            <span className="adm-list__time">{timeAgo(c.createdAt)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="adm-empty">
                        <i className="fas fa-inbox" aria-hidden="true"></i>
                        <p>{loading ? 'Loading…' : 'No messages yet.'}</p>
                      </div>
                    )}
                  </article>

                  <article className="adm-card">
                    <header className="adm-card__head">
                      <div>
                        <h2>Quick actions</h2>
                        <p>Common admin tasks</p>
                      </div>
                    </header>
                    <div className="adm-actions">
                      {quickActions.map((a) => (
                        <button key={a.label} type="button" className="adm-action" onClick={() => selectSection(a.section)}>
                          <i className={`fas ${a.icon}`} aria-hidden="true"></i>
                          <span>{a.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="adm-catalog">
                      <div>
                        <small>Catalog</small>
                        <strong>{value(stats.products)} products</strong>
                      </div>
                      <Link to="/shop" className="adm-link">
                        Open shop <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
                      </Link>
                    </div>
                  </article>
                </div>
              </section>
            )}

            {displaySection !== 'Dashboard' && (
              <div className="adm-section">
                {displaySection === 'Orders' && <OrdersManagement />}
                {displaySection === 'Products' && <ProductsManagement />}
                {displaySection === 'Inventory' && (
                  <InventoryManagement onManageProducts={() => selectSection('Products')} />
                )}
                {displaySection === 'Users' && <UsersManagement />}
                {displaySection === 'Contacts' && (
                  <ContactsManagement
                    onContactsChange={(contacts) => setData((prev) => ({ ...prev, contacts }))}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}

export default Dashboard;
